import { Button, Flex, Input, Modal, Text } from '@comet/blocks';
import { queryClient } from '@comet/query';
import { useNavigate, useParams } from '@comet/router';
import { styled } from '@comet/styled';
import { useQuery } from '@tanstack/react-query';
import { notification } from 'antd';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiAlertLine } from 'react-icons/ri';
import { sendErrorNotification } from 'src/blocks/Notification';
import {
  resendOTPToDeleteSA,
  sendOTPToDeleteSA,
  verifyOTPToDeleteSA,
} from '../service';
import { StorageAccountInList } from '@comet/pages/Project/StorageAccounts/types';

const StyledAlertIcon = styled(RiAlertLine)`
  color: ${({ theme }) => theme.palette.accent.red.default};
  font-size: 28px;
`;

interface IDeleteOrganisationModalProps {
  saData: StorageAccountInList;
  toggleModal: () => void;
}

export const DeleteSAModal = memo(
  ({ saData, toggleModal }: IDeleteOrganisationModalProps) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [otpSent, setOTPSent] = useState(false);
    const [otpError, setOTPError] = useState('');
    const [otpInput, setotpInput] = useState('');
    const [disableOTPTimer, setDisableOTPTimer] = useState(0);
    const timerRef = useRef<NodeJS.Timer>();
    const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newOtp = e.target.value;
      if (newOtp.length > 0 && (isNaN(parseInt(newOtp)) || newOtp.length > 6)) {
        return;
      }
      setotpInput(newOtp);
      setOTPError('');
    };

    const onSendOTP = useCallback(() => {
      setOTPSent(true);
      setDisableOTPTimer(60);
      timerRef.current = setInterval(() => {
        setDisableOTPTimer((prev) => prev - 1);
      }, 1000);
    }, []);

    useEffect(() => {
      if (disableOTPTimer <= 0) {
        clearInterval(timerRef.current);
      }
    }, [disableOTPTimer]);

    const { refetch: sendOtp } = useQuery({
      queryKey: ['delete-sa-send-otp'],
      queryFn: () => sendOTPToDeleteSA(projectId, saData?.id),
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: onSendOTP,
      onError: (error: any) => {
        sendErrorNotification({
          message: 'Error',
          description: error.response.data.message,
          reportBug: true,
        });
      },
    });

    const { refetch: resendOtp } = useQuery({
      queryKey: ['delete-sa-resend-otp'],
      queryFn: () => resendOTPToDeleteSA(projectId, saData?.id),
      enabled: false,
      refetchOnWindowFocus: false,
      onSuccess: onSendOTP,
      onError: (error: any) => {
        sendErrorNotification({
          message: 'Error',
          description: error.response.data.message,
          reportBug: true,
        });
      },
    });

    const { refetch: verifyOtp } = useQuery({
      queryKey: ['delete-sa-verify-otp'],
      queryFn: () => verifyOTPToDeleteSA(projectId, saData?.id, otpInput),

      enabled: false,
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        setOTPError(error.response.data.message);
      },
      onSuccess: (data) => {
        if (data.data.verified) {
          notification.success({
            message: 'Success',
            description: 'Bucket is deleted successfully',
          });
          queryClient.invalidateQueries({
            queryKey: ['useGetStorageAccountsQuery'],
          });
        } else {
          setOTPError('Invalid OTP');
        }
      },
    });

    const onDelete = async () => {
      if (!otpSent) {
        sendOtp();
        return;
      }
      verifyOtp();
    };
    const isValidOTPInput = useMemo(() => {
      if (!otpSent) {
        return true;
      }
      return otpInput.length === 6 && !isNaN(parseInt(otpInput));
    }, [otpSent, otpInput]);
    return (
      <Modal
        className="deletion-modal"
        width={560}
        title={null}
        open
        closable={false}
        destroyOnClose={true}
        footer={null}
        okButtonProps={{
          type: 'primary',
        }}
      >
        <Flex gap={16} direction="column">
          <Flex
            direction="row"
            gap={16}
            padding={12}
            style={{
              backgroundColor: 'rgba(181, 45, 67, 1)',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <Flex>
              <StyledAlertIcon
                style={{ height: 20, width: 20, color: '#fff' }}
              />
            </Flex>
            <Flex gap={10} direction="column">
              <Text appearance="body.semiBold" color="neutral.white">
                Delete Bucket
              </Text>
              <Text appearance="body.regular" color="neutral.white">
                Are you sure you want to delete this bucket?{' '}
              </Text>
            </Flex>
          </Flex>
          <Flex direction="column" style={{ paddingInline: 20 }}>
            <Text appearance="body.semiBold" style={{ marginTop: 12 }}>
              When you delete an bucket, this immediately happens:
            </Text>
            <ul>
              <li>All resources in the bucket will be deleted permanently.</li>
              <li>
                You will not be able to access the bucket again, unless you
                create a new one.
              </li>
            </ul>
            <Flex style={{ marginTop: 12, width: '100%' }} direction="column">
              <Text appearance="label.short.semiBold">Name of your bucket</Text>
              <Input
                disabled
                style={{
                  marginTop: 8,
                  width: '100%',
                  borderRadius: 8,
                  padding: 8,
                  borderWidth: 1,
                  fontWeight: 'bold',
                }}
                value={saData?.name}
              />
            </Flex>
            {otpSent && (
              <Flex style={{ marginTop: 12, width: '100%' }} direction="column">
                <Text appearance="label.short.semiBold">
                  Enter OTP to continue
                </Text>
                <Input
                  style={{
                    marginTop: 8,
                    width: '100%',
                    borderRadius: 8,
                    padding: 8,
                    borderWidth: 1,
                    borderColor: otpError.length
                      ? 'rgba(181, 45, 67, 1)'
                      : undefined,
                    color: otpError.length ? 'rgba(181, 45, 67, 1)' : undefined,
                    fontWeight: 'bold',
                  }}
                  value={otpInput}
                  onChange={onOtpChange}
                />
                {otpError.length > 0 && (
                  <Text
                    appearance="label.short.regular"
                    style={{ color: 'rgba(181, 45, 67, 1)', marginTop: 4 }}
                  >
                    {otpError}
                  </Text>
                )}
              </Flex>
            )}
            {otpSent && (
              <div
                onClick={() => {
                  if (!disableOTPTimer) {
                    resendOtp();
                  }
                }}
                style={{
                  cursor: disableOTPTimer ? 'not-allowed' : 'pointer',
                  alignSelf: 'flex-end',
                  marginTop: 4,
                  color: disableOTPTimer ? '#9a9a9a' : undefined,
                }}
              >
                Resend OTP{' '}
                {disableOTPTimer
                  ? `after ${disableOTPTimer} second${
                      disableOTPTimer > 1 ? 's' : ''
                    }`
                  : ''}
              </div>
            )}
            <Flex gap={8} justifyContent="end" style={{ marginBlock: 20 }}>
              <Button
                onClick={toggleModal}
                appearance="transparent"
                htmlType="button"
              >
                Cancel
              </Button>
              <Button
                onClick={onDelete}
                appearance="alert"
                htmlType="button"
                disabled={!isValidOTPInput || !!otpError}
              >
                {otpSent ? 'Delete Bucket' : 'Send OTP'}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>
    );
  }
);
